import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Sparkles, LogIn, Activity, Calculator } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../../locales/translations";
import { saveLanguagePreference } from "../../lib/languageDetection";
import CryptoVIPLogo from "../ui/CryptoVIPLogo";

// Default translations for standalone pages
const defaultTranslations = {
  header: {
    home: "Home",
    statistics: "Statistics",
    guides: "Guides",
    blog: "Blog",
    faq: "FAQ",
    login: "Login",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Calculator"
  }
};

// Translations for header in all languages
const headerTranslations = {
  en: {
    home: "Home",
    statistics: "Statistics",
    guides: "Guides",
    blog: "Blog",
    faq: "FAQ",
    login: "Login",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Calculator"
  },
  de: {
    home: "Startseite",
    statistics: "Statistiken",
    guides: "Ratgeber",
    blog: "Blog",
    faq: "FAQ",
    login: "Anmelden",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Rechner"
  },
  nl: {
    home: "Home",
    statistics: "Statistieken",
    guides: "Gidsen",
    blog: "Blog",
    faq: "FAQ",
    login: "Inloggen",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Calculator"
  },
  da: {
    home: "Hjem",
    statistics: "Statistik",
    guides: "Guider",
    blog: "Blog",
    faq: "FAQ",
    login: "Log ind",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Beregner"
  },
  "en-ca": {
    home: "Home",
    statistics: "Statistics",
    guides: "Guides",
    blog: "Blog",
    faq: "FAQ",
    login: "Login",
    joinVip: "Join CVC",
    whales: "Whale Tracker",
    cashback: "Cashback Calculator"
  }
};

export const LocalizedHeader = ({ t, currentLang }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const langMenuRef = React.useRef(null);
  const location = useLocation();
  
  // Auto-detect language from URL if not provided
  const pathLang = location.pathname.split('/')[1];
  const detectedLang = ['en', 'de', 'nl', 'en-ca', 'da'].includes(pathLang) ? pathLang : 'en';
  const effectiveLang = currentLang || detectedLang;
  
  // Get header translations for current language
  const headerT = headerTranslations[effectiveLang] || headerTranslations.en;

  const currentLangInfo = SUPPORTED_LANGUAGES.find(l => l.code === effectiveLang) || SUPPORTED_LANGUAGES[0];

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (langPath) => {
    saveLanguagePreference(langPath);
    setLangMenuOpen(false);
  };

  return (
    <header
      data-testid="header-nav"
      className="fixed top-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Left */}
          <Link to={currentLangInfo.path} className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link" aria-label="CryptoVIP Club - Home">
            <CryptoVIPLogo className="h-9" />
          </Link>

          {/* Desktop Navigation - Absolute Center */}
          <nav className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2" role="navigation" aria-label="Main navigation">
            <Link to={currentLangInfo.path} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300" data-testid="nav-home">
              {headerT.home}
            </Link>
            <Link to={`${currentLangInfo.path}/whale-tracker`} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300 flex items-center gap-1" data-testid="nav-whales">
              <Activity className="w-3.5 h-3.5" />{headerT.whales}
            </Link>
            <Link to={`${currentLangInfo.path}/cashback-calculator`} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300 flex items-center gap-1" data-testid="nav-cashback">
              <Calculator className="w-3.5 h-3.5" />{headerT.cashback}
            </Link>
            <Link to={`${currentLangInfo.path}/statistics`} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300" data-testid="nav-statistics">
              {headerT.statistics}
            </Link>
            <Link 
              to={`${currentLangInfo.path}/guides`} 
              className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300" 
              data-testid="nav-guides"
            >
              {headerT.guides}
            </Link>
            <Link to={`${currentLangInfo.path}/blog`} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300" data-testid="nav-blog">
              {headerT.blog}
            </Link>
          </nav>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center gap-4 flex-shrink-0">
          {/* Join VIP Club Button - Golden Gradient with Purple Shimmer */}
          <Link to="/register" aria-label="Join VIP Club - Register">
            <button
              data-testid="nav-join-btn"
              className="relative flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black hover:shadow-lg hover:shadow-[#D4AF37]/25 transition-all duration-300 hover:scale-105 overflow-hidden"
            >
              {/* Purple shimmer sweep animation */}
              <span className="absolute inset-0 overflow-hidden rounded-full">
                <span 
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-[#A855F7]/70 to-transparent"
                  style={{
                    animation: 'shimmer 2s infinite',
                    transform: 'translateX(-100%)',
                  }}
                />
              </span>
              <Sparkles className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{headerT.joinVip}</span>
            </button>
          </Link>
          
          <style>{`
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}</style>

          {/* Language Selector with Globe */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-white transition-all rounded-full hover:bg-white/5 border border-transparent hover:border-white/10"
              data-testid="language-selector"
              aria-label="Select language"
              aria-expanded={langMenuOpen}
              aria-haspopup="listbox"
            >
              <Globe className="w-4 h-4" aria-hidden="true" />
              <span className="font-medium">{currentLangInfo.flag}</span>
              <svg className={`w-3 h-3 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {langMenuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-[#18181B] rounded-xl py-2 shadow-2xl border border-white/10 overflow-hidden" role="listbox" aria-label="Available languages">
                <div className="px-4 py-2 border-b border-white/5">
                  <span className="text-xs text-[#71717A] uppercase tracking-wider">Select Language</span>
                </div>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Link
                    key={lang.code}
                    to={lang.path}
                    onClick={() => handleLanguageSelect(lang.path)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      lang.code === effectiveLang 
                        ? "bg-[#A855F7]/10 text-[#A855F7]" 
                        : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`lang-option-${lang.code}`}
                    role="option"
                    aria-selected={lang.code === effectiveLang}
                  >
                    <span className="text-lg" aria-hidden="true">{lang.flag}</span>
                    <span className="flex-1">{lang.name}</span>
                    {lang.code === effectiveLang && (
                      <svg className="w-4 h-4 text-[#A855F7]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
        </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <nav className="md:hidden bg-[#0A0A0A] border-t border-white/5 px-6 py-4 space-y-3" role="navigation" aria-label="Mobile navigation">
          <Link to={currentLangInfo.path} className="block text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            {headerT.home}
          </Link>
          <Link to={`${currentLangInfo.path}/whale-tracker`} className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            <Activity className="w-4 h-4" />{headerT.whales}
          </Link>
          <Link to={`${currentLangInfo.path}/cashback-calculator`} className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            <Calculator className="w-4 h-4" />{headerT.cashback}
          </Link>
          <Link to={`${currentLangInfo.path}/statistics`} className="block text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            {headerT.statistics}
          </Link>
          <Link to={`${currentLangInfo.path}/guides`} className="block text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            {headerT.guides}
          </Link>
          <Link to={`${currentLangInfo.path}/blog`} className="block text-sm text-[#A1A1AA] hover:text-white py-2" onClick={() => setMobileOpen(false)}>
            {headerT.blog}
          </Link>
          
          {/* Mobile Language Selector */}
          <div className="pt-4 border-t border-white/10">
            <span className="text-xs text-[#52525B] uppercase tracking-wider mb-3 block" id="mobile-lang-label">
              <Globe className="w-3 h-3 inline mr-2" />
              Language
            </span>
            <div className="flex flex-wrap gap-2" role="listbox" aria-labelledby="mobile-lang-label">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Link
                  key={lang.code}
                  to={lang.path}
                  onClick={() => {
                    handleLanguageSelect(lang.path);
                    setMobileOpen(false);
                  }}
                  className={`px-4 py-2 text-sm rounded-full flex items-center gap-2 transition-colors ${
                    lang.code === effectiveLang 
                      ? "bg-[#A855F7] text-white" 
                      : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                  }`}
                  role="option"
                  aria-selected={lang.code === effectiveLang}
                  aria-label={lang.name}
                >
                  <span>{lang.flag}</span>
                  <span className="text-xs">{lang.code.toUpperCase()}</span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Mobile Action Buttons */}
          <div className="pt-4 space-y-3">
            <Link 
              to="/login" 
              className="flex items-center justify-center gap-2 w-full py-3 text-sm text-[#A1A1AA] border border-white/10 rounded-full hover:bg-white/5 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <LogIn className="w-4 h-4" />
              <span>{headerT.login}</span>
            </Link>
            <Link 
              to="/register" 
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black rounded-full hover:shadow-lg hover:shadow-[#D4AF37]/25 transition-all"
              onClick={() => setMobileOpen(false)}
            >
              <Sparkles className="w-4 h-4" />
              <span>{headerT.joinVip}</span>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default LocalizedHeader;
